import {
    Directive,
    ElementRef,
    forwardRef,
    OnInit,
    Renderer2,
} from '@angular/core';
import { NG_VALUE_ACCESSOR } from '@angular/forms';
import { WdxBaseInputControlsValueAccessorClass } from '@wdx/shared/components/abstract-classes';
import { DateTime } from 'luxon';
import { FULL_DAY, TIME } from './wdx-input-date.static';

export type DateInputType = 'date' | 'datetime-local' | 'month';

@Directive({
    selector: 'input[wdxInputDate]',
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            // tslint:disable-next-line:no-forward-ref no-use-before-declare
            useExisting: forwardRef(() => WdxInputDateDirective),
            multi: true,
        },
    ],
    host: {
        '(input)': 'onInput($event.target.value)',
    },
    standalone: false,
})
export class WdxInputDateDirective
    extends WdxBaseInputControlsValueAccessorClass
    implements OnInit
{
    inputType?: DateInputType;

    constructor(
        private elementRef: ElementRef<HTMLInputElement>,
        private renderer: Renderer2,
    ) {
        super();
    }

    ngOnInit(): void {
        // currently only supports initial type
        // will need changing if we allow input 'type' to be dynamically updated from the component
        this.inputType = this.elementRef.nativeElement.type as DateInputType;
        this.setMaxAttribute();
    }

    onInput(value: any): void {
        const sanitizedValue = this.sanitizeDateInputValue(value);

        if (sanitizedValue !== value) {
            this.renderer.setProperty(
                this.elementRef.nativeElement,
                'value',
                sanitizedValue,
            );
        }

        let val: string | null = sanitizedValue;

        switch (this.inputType) {
            case 'date':
            case 'month':
                val = sanitizedValue;
                break;
            case 'datetime-local': {
                const DATE = DateTime.fromJSDate(new Date(sanitizedValue));
                val = DATE.isValid ? DATE.toISO() : null;
                break;
            }
            default:
                val = sanitizedValue;
                break;
        }

        this.val = val;
        this.onChange(val);
    }

    override writeValue(value: any): void {
        let val = value;

        if (val) {
            const DATE = DateTime.fromJSDate(new Date(value));

            switch (this.inputType) {
                case 'datetime-local':
                    val = `${DATE.toFormat(FULL_DAY)}T${DATE.toFormat(TIME)}`;
                    break;
                case 'month':
                    val = DATE.toFormat('yyyy-MM');
                    break;
                default:
                    val = DATE.toUTC()
                        .set({ second: 0, millisecond: 0 })
                        .toFormat(FULL_DAY);
                    break;
            }

            this.renderer.setProperty(
                this.elementRef.nativeElement,
                'value',
                val,
            );
        } else if (val === null) {
            this.renderer.setProperty(
                this.elementRef.nativeElement,
                'value',
                val,
            );
        }

        this.val = val;
    }

    private setMaxAttribute() {
        const maxAttr = this.elementRef.nativeElement.max;
        if (maxAttr || !this.inputType) {
            return;
        }

        const maxDate = this.getMaxDateForType(this.inputType);
        if (!maxDate) {
            return;
        }

        this.renderer.setAttribute(
            this.elementRef.nativeElement,
            'max',
            maxDate,
        );
    }

    private getMaxDateForType(type: DateInputType): string | null {
        switch (type) {
            case 'date':
                return '9999-12-31';
            case 'datetime-local':
                return '9999-12-31T23:59';
            case 'month':
                return '9999-12';
            default:
                return null;
        }
    }

    private sanitizeDateInputValue(value: string): string {
        if (!value || !this.inputType) {
            return value;
        }

        switch (this.inputType) {
            case 'date':
            case 'datetime-local':
            case 'month':
                return this.trimYearToFourDigits(value);
            default:
                return value;
        }
    }

    private trimYearToFourDigits(value: string): string {
        const [datePart, timePart] = value.split('T');
        const dateSegments = datePart.split('-');

        if (!dateSegments[0] || dateSegments[0].length <= 4) {
            return value;
        }

        dateSegments[0] = dateSegments[0].slice(0, 4);
        const trimmedDatePart = dateSegments.join('-');

        return timePart === undefined
            ? trimmedDatePart
            : `${trimmedDatePart}T${timePart}`;
    }
}
